import React, { useRef, useState, useEffect } from 'react';

interface ImageUploaderProps {
  label: string;
  currentPreview?: string | null;
  file: File | null;
  onChange: (file: File | null, preview: string | null) => void;
  name: string;
  errors?: string[];
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  label,
  currentPreview,
  file,
  onChange,
  name,
  errors,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(currentPreview || null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target?.result as string;
        setPreview(previewUrl);
        onChange(file, previewUrl);
      };
      reader.readAsDataURL(file);
    } else if (currentPreview) {
      setPreview(currentPreview);
    }
  }, [file, currentPreview, onChange]);

  const handleFileChange = (selectedFile: File | null) => {
    if (!selectedFile) {
      onChange(null, null);
      setPreview(null);
      return;
    }

    // ファイル形式のバリデーション
    if (!selectedFile.type.match(/^image\/(png|jpeg|jpg)$/)) {
      alert('画像ファイルはPNG、JPEG、JPG形式のみ対応しています');
      return;
    }

    // ファイルサイズのバリデーション（5MB）
    if (selectedFile.size > 5 * 1024 * 1024) {
      alert('画像ファイルは5MB以下にしてください');
      return;
    }

    onChange(selectedFile, null); // previewはuseEffectで設定される
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    handleFileChange(selectedFile);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemove = () => {
    onChange(null, null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="form-group mb-3">
      <label className="form-label fw-semibold">{label}</label>
      {preview && (
        <div className="mb-2">
          <img
            src={preview}
            alt="Preview"
            className="img-thumbnail"
            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
          />
        </div>
      )}
      <div
        className={`border rounded p-3 ${isDragging ? 'border-primary' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ cursor: 'pointer' }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          name={name}
          accept="image/*"
          onChange={handleInputChange}
          className="d-none"
        />
        <div className="text-center">
          <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
          <p className="mb-0">
            {isDragging ? 'ここにドロップしてください' : 'クリックまたはドラッグ&ドロップで画像をアップロード'}
          </p>
        </div>
      </div>
      {preview && (
        <button
          type="button"
          className="btn btn-sm btn-outline-danger mt-2"
          onClick={handleRemove}
        >
          <i className="fas fa-times"></i> 画像を削除
        </button>
      )}
      <small className="form-text text-muted">ロゴ画像をアップロード（PNG、JPEG、JPG形式、5MB以下）</small>
      {errors && errors.length > 0 && (
        <div className="invalid-feedback d-block">{errors.join(', ')}</div>
      )}
    </div>
  );
};

export default ImageUploader;



